import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import TranslatableInput from '../../Common/TranslatableInput';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import LanguageSwitch from '../../Common/LanguageSwitch';
import CommonInput from '../../Common/CommonInput';
import ItemInput from '../../Common/ItemInput';
import CommonSelect from '../../Common/CommonSelect';
import CommonAsyncSelect from '../../Common/CommonAsyncSelect';
import CommonDate from '../../Common/CommonDate';
import CommonCheckbox from '../../Common/CommonCheckbox';

import { fetchSettings } from '../../../store/modules/purchaseinvoice/purchaseinvoiceActions';
import useFormSettings from '../../../hooks/useFormSettings';

import {Tab, Nav} from 'react-bootstrap';
import { 
    calculateItemValues, 
    calculateGrossTotal, 
    calculateOverallDiscount, 
    distributeHeaderDiscount,
    calculateTotals,
    calculateTotalCharges,
    distributeTotalCharges,
    calculateFinalTotals,
    calculateItemLandedCosts,
    calculateOverallAmounts,
    roundLineLcy,
    summarizeLineLcyAmounts
} from './PurchaseInvoiceCalculation';

import supplierService from '../../../services/supplierService';

import useModal from '../../../hooks/useModal';
import DataSelectionModal from '../../Common/CommonModal/DataSelectionModal';
import Items from '../../../pages/Items/Items'; // Adjust the path as needed
import { useHasPermission } from '../../../utils/permissionUtils';

const PurchaseInvoiceForm = ({ initialData, onSave, isLoading, onClose, customActionsDispatch = {} }) => {

    const hasCreatePermission = useHasPermission(['create purchase invoices']);
    const hasUpdatePermission = useHasPermission(['update purchase invoices']);

    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('purchaseinvoice', fetchSettings);
    const { locations, countries, currencies, selected, additional_charges } = settings || {};
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [typingTimer, setTypingTimer]         = useState(null);
    const doneTypingInterval = 800;

    const [formData, setFormData] = useState({
        id: null,
        invoice_number: '',
        invoice_date: '',
        supplier: null,
        country: null,
        location: null,
        currency: null,
        address1: {},
        address2: {},
        conversion_rate: '',
        purchase_number: '',
        po_date: '',
        quotation_number: '',
        quotation_date: '',
        sup_order_number: '',
        sup_invoice_number: '',
        phone_number: '',
        reference: {},
        remarks: {},
        terms: {},
        post_date: new Date().toISOString().split('T')[0],
        is_active: '',
        gross_total: '',
        gross_total_lcy: '',
        discount_percentage: '',
        discount_amount: '',
        discount_amount_lcy: '',
        sub_total: '',
        sub_total_lcy: '',
        vat_amount: '',
        vat_amount_lcy: '',
        net_total: '',
        net_total_lcy: '',
        total_charges: '',
        total_charges_lcy: '',
        supplier_charges: '',
        supplier_charges_lcy: '',
        supplier_total: '',
        supplier_total_lcy: '',
        total_cost: '',
        total_cost_lcy: '',
        additional_charges: [],
        vat_business_group: null,
        is_domestic: true,
        is_posted: false,
        posted_invoice: false,
        items: []

    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [showItemModal, setShowItemModal] = useState(false);

    const [selectedItems, setSelectedItems] = useState([]);
    const lastSelectionRef = useRef(null);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                id: initialData.id || null,
                invoice_number: initialData.invoice_number || '',
                invoice_date: initialData.invoice_date || '',
                supplier: initialData.supplier || null,
                country: initialData.country || null,
                location: (initialData.initialType === 'create' && locations && locations.length > 0) ? 
                    locations.find(location => location.is_primary) : 
                    (initialData.location || null),
                currency: initialData.currency || null,
                address1: initialData.address1 || {},
                address2: initialData.address2 || {},
                conversion_rate: initialData.conversion_rate || '',
                purchase_number: initialData.purchase_number || '',
                po_date: initialData.po_date || '',
                quotation_number: initialData.quotation_number || '',
                quotation_date: initialData.quotation_date || '',
                sup_order_number: initialData.sup_order_number || '',
                sup_invoice_number: initialData.sup_invoice_number || '',
                phone_number: initialData.phone_number || '',
                reference: initialData.reference || {},
                remarks: initialData.remarks || {},
                terms: initialData.terms || {},
                is_posted: initialData.is_posted || false,
                posted_invoice: initialData.is_posted || false,
                post_date: initialData.post_date || new Date().toISOString().split('T')[0],
                is_active: initialData.is_active || '',
                gross_total: initialData.gross_total || '',
                gross_total_lcy: initialData.gross_total_lcy || '',
                discount_percentage: initialData.discount_percentage || '',
                discount_amount: initialData.discount_amount || '',
                discount_amount_lcy: initialData.discount_amount_lcy || '',
                sub_total: initialData.sub_total || '',
                sub_total_lcy: initialData.sub_total_lcy || '',
                vat_amount: initialData.vat_amount || '',
                vat_amount_lcy: initialData.vat_amount_lcy || '',
                net_total: initialData.net_total || '',
                net_total_lcy: initialData.net_total_lcy || '',
                total_charges: initialData.total_charges || '',
                total_charges_lcy: initialData.total_charges_lcy || '',
                supplier_charges: initialData.supplier_charges || '',
                supplier_charges_lcy: initialData.supplier_charges_lcy || '',
                supplier_total: initialData.supplier_total || '',
                supplier_total_lcy: initialData.supplier_total_lcy || '',
                total_cost: initialData.total_cost || '',
                total_cost_lcy: initialData.total_cost_lcy || '',
                items: initialData.items || [],
                additional_charges: initialData.initialType === 'create' ? additional_charges : (initialData.additional_charges || []),
                vat_business_group: initialData.vat_business_group || null,
                is_domestic: initialData.is_domestic || true
            });
        }
    }, [initialData, additional_charges]);

    const calculateFormData = (formData, conversionRate) => {
        const grossTotal = calculateGrossTotal(formData.items);
        const overallDiscountAmount = calculateOverallDiscount(
            grossTotal,
            parseFloat(formData.discount_percentage) || 0,
            parseFloat(formData.discount_amount) || 0
        );
        const itemsWithHeaderDiscount = distributeHeaderDiscount(
            formData.items,
            overallDiscountAmount
        );
        const totals = calculateTotals(itemsWithHeaderDiscount);
        const totalCharges = calculateTotalCharges(formData.additional_charges, conversionRate);
        const itemsWithLandedCosts = distributeTotalCharges(
            itemsWithHeaderDiscount,
            totalCharges
        );
        const finalTotals = calculateFinalTotals(
            itemsWithLandedCosts,
            formData.additional_charges,
            conversionRate
        );
        const overallAmounts = calculateOverallAmounts(
            grossTotal,
            overallDiscountAmount,
            totals.vatAmount,
            totals.netTotal,
            conversionRate
        );
        const lineLcyTotals = summarizeLineLcyAmounts(itemsWithLandedCosts, conversionRate);
        const itemsWithLandedCostsLCY = itemsWithLandedCosts.map(item => 
            calculateItemLandedCosts(item, conversionRate)
        );

        return {
            gross_total: grossTotal.toFixed(3),
            gross_total_lcy: overallAmounts.gross_total_lcy,
            discount_amount: overallDiscountAmount.toFixed(3),
            discount_amount_lcy: overallAmounts.discount_amount_lcy,
            sub_total: (grossTotal - overallDiscountAmount).toFixed(3),
            sub_total_lcy: lineLcyTotals.sub_total_lcy.toFixed(3),
            vat_amount: totals.vatAmount.toFixed(3),
            vat_amount_lcy: lineLcyTotals.vat_amount_lcy.toFixed(3),
            net_total: totals.netTotal.toFixed(3),
            net_total_lcy: lineLcyTotals.net_total_lcy.toFixed(3),
            total_charges: finalTotals.total_charges,
            total_charges_lcy: (parseFloat(finalTotals.total_charges) * conversionRate).toFixed(3),
            supplier_total: finalTotals.supplier_total,
            supplier_total_lcy: (parseFloat(finalTotals.supplier_total) * conversionRate).toFixed(3),
            total_cost: finalTotals.total_cost,
            total_cost_lcy: (parseFloat(finalTotals.total_cost) * conversionRate).toFixed(3),
            items: itemsWithLandedCostsLCY.map(item => ({
                ...item,
                line_amount_lcy: roundLineLcy(item.line_amount, conversionRate).toFixed(3),
                discount_amount_lcy: roundLineLcy(item.discount_amount, conversionRate).toFixed(3),
                header_discount_lcy: roundLineLcy(item.header_discount, conversionRate).toFixed(3),
                amount_excl_vat_lcy: roundLineLcy(item.amount_excl_vat, conversionRate).toFixed(3),
                vat_amount_lcy: roundLineLcy(item.vat_amount, conversionRate).toFixed(3),
                amount_incl_vat_lcy: roundLineLcy(item.amount_incl_vat, conversionRate).toFixed(3)
            }))
        };
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        const itemFieldMatch = name.match(/^items\[(\d+)\]\.(.+)$/);
        const additionalFieldMatch = name.match(/^additional_charges\[(\d+)\]\.(.+)$/);
        
        setFormData(prev => {
            let newData = { ...prev };
            const conversionRate = name === 'conversion_rate' 
                ? parseFloat(value) || 1 
                : parseFloat(prev.conversion_rate) || 1;

            // Validate discount_percentage
            if (name === 'discount_percentage') {
                const numValue = parseFloat(value) || 0;
                if (numValue > 100 || numValue < 0) {
                    return prev; // Don't update if value exceeds 100 or is negative
                }
            }
            
            if (itemFieldMatch) {
                const itemIndex = parseInt(itemFieldMatch[1]);
                const field = itemFieldMatch[2];
                
                if(field === 'discount_percentage') {
                    const numValue = parseFloat(value) || 0;
                    if (numValue > 100 || numValue < 0) {
                        return prev; // Don't update if value exceeds 100 or is negative
                    }
                }
                
                const newItems = [...prev.items];
                const updatedItem = {
                    ...newItems[itemIndex],
                    [field]: type === 'checkbox' ? checked : value
                };

                // Only reset calculated values if the field is truly empty (not zero)
                if (['quantity_received', 'unit_price'].includes(field) && (value === '' || value === 0 || value === '0')) {
                    updatedItem.line_amount = '0.000';
                    updatedItem.vat_amount = '0.000';
                    updatedItem.header_discount = '0.000';
                    updatedItem.landed_cost = '0.000';
                    updatedItem.landed_line_total = '0.000';
                    updatedItem.landed_cost_lcy = '0.000';
                    updatedItem.landed_line_total_lcy = '0.000';
                    updatedItem.amount_excl_vat = '0.000';
                    updatedItem.amount_incl_vat = '0.000';
                } else if (['quantity_received', 'unit_price', 'discount_percentage'].includes(field)) {
                    const calculatedValues = calculateItemValues(updatedItem);
                    Object.assign(updatedItem, calculatedValues);
                }

                newItems[itemIndex] = updatedItem;
                newData.items = newItems;
            } 
            else if (additionalFieldMatch) {
                const additionalIndex = parseInt(additionalFieldMatch[1]);
                const field = additionalFieldMatch[2];
                
                const newAdditionalCharges = [...prev.additional_charges];
                const newValue = type === 'checkbox' ? checked : (value === '' ? '0.000' : value);
                newAdditionalCharges[additionalIndex] = {
                    ...newAdditionalCharges[additionalIndex],
                    [field]: newValue
                };
                newData.additional_charges = newAdditionalCharges;
            } 
            else {
                newData[name] = type === 'checkbox' ? checked : value;
            }

            const calculatedData = calculateFormData(newData, conversionRate);
            return {
                ...newData,
                ...calculatedData
            };
        });
    };

    const handleDiscountChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            return {
                ...prev,
                [name]: value
            }
        });

        if (value.length > 0) {
            clearTimeout(typingTimer);
            const timer = setTimeout(() => doneTyping(value, name), doneTypingInterval);
            setTypingTimer(timer);
        } else {
            doneTyping(value, name);
            clearTimeout(typingTimer);
        }
    }

    const doneTyping = async (value, name) => {
        setFormData(prev => {
            let newData = { ...prev };
            const conversionRate = parseFloat(prev.conversion_rate) || 1;

            newData[name] = value;
            if(prev.gross_total*1 < value*1) {
                newData[name] = prev.gross_total;
            }

            const calculatedData = calculateFormData(newData, conversionRate);
            return {
                ...newData,
                ...calculatedData
            }; 
        });
    };

    
    const handleSelectChange = (name, value) => {
        if(name === 'currency') {
            setFormData(prev => {
                const conversionRate = value ? parseFloat(value?.conversion_rate) || 1 : prev.conversion_rate;
                const newData = {
                    ...prev,
                    [name]: value,
                    conversion_rate: value?.conversion_rate || prev.conversion_rate
                };
                const calculatedData = calculateFormData(newData, conversionRate);
                return {
                    ...newData,
                    ...calculatedData
                };
            });
        }
        
        if(name === 'supplier') {
            setFormData(prev => {
                const conversionRate = value ? parseFloat(value?.currency?.conversion_rate) || 1 : prev.conversion_rate;
                const isDomestic = value?.is_domestic !== undefined ? value.is_domestic : prev.is_domestic;
                
                // Update items with new VAT percentage based on is_domestic
                const updatedItems = prev.items.map(item => {
                    const vatPercentage = isDomestic 
                        ? item.vat_product_group?.vat_percentage || '0.000'
                        : '0.000';
                    
                    // Recalculate item values with new VAT percentage
                    const calculatedValues = calculateItemValues({
                        ...item,
                        vat_percentage: vatPercentage
                    });

                    return {
                        ...item,
                        vat_percentage: vatPercentage,
                        ...calculatedValues
                    };
                });

                const newData = {
                    ...prev,
                    [name]: value,
                    currency: value?.currency || prev.currency,
                    country: value?.country || prev.country,
                    conversion_rate: value?.currency?.conversion_rate || prev.conversion_rate,
                    vat_business_group: value?.vat_group ? {
                        id: value.vat_group.id,
                        label: value.vat_group.label,
                    } : prev.vat_business_group,
                    is_domestic: (value?.is_domestic === 0) ? false : true || prev.is_domestic,
                    items: updatedItems,
                    address1: value?.address_name,
                    address2: value?.address,
                };

                const calculatedData = calculateFormData(newData, conversionRate);
                return {
                    ...newData,
                    ...calculatedData
                };
            });
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isCreateMode = !formData?.id;
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;

    const isSubmitting = isLoading || isSaving || formData.is_posted || !hasPermission;
    const selectedLang = languages.find(l => l.code === selectedLanguage);

    const itemModal = useModal();
    const handleItemSelected = (selectedItems) => {
        // Get current items from form data
        const currentItems = formData.items || [];
        
        // Filter out items that already exist in the form
        const newItems = selectedItems.filter(selectedItem => 
            !currentItems.some(existingItem => existingItem.item?.id === selectedItem.id)
        );
        
        // Transform new items to match the form data structure
        const transformedItems = newItems.map(item => ({
                id: crypto.randomUUID(),
                item: {
                    id: item.id,
                    item_code: item.item_code,
                    label: item.name?.en || '',
                },
                unit: item.measurement_unit?.label || '',
                quantity_received : '',
                quantity_ordered : '',
                unit_price: item.unit_price || '0.000',
                discount_percentage : '',
                line_amount : '',
                vat_amount : '',
                header_discount : '',
                landed_cost : '',
                landed_line_total : '',
                landed_cost_lcy : '',
                landed_line_total_lcy : '',
                amount_excl_vat : '',
                amount_incl_vat : '',
                vat_business_group: JSON.stringify({}), // Convert object to string
                vat_product_group: {
                    id: item.vat_group?.id,
                    label: item.vat_group?.label,
                    vat_percentage: item.vat_group?.vat_percentage || '0.000',
                }, // Convert object to string
                vat_percentage: (formData.is_domestic) ? item.vat_group?.vat_percentage || '0.000' : '0.000',
                location : JSON.stringify({}), // Convert object to string
            }
        ));

        // Update form data with new items
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, ...transformedItems]
        }));
    };





    const handleRemoveItem = (index) => {
        setFormData(prev => {
            const newItems = [...prev.items];
            newItems.splice(index, 1);
            
            const newData = {
                ...prev,
                items: newItems
            };

            const conversionRate = parseFloat(prev.conversion_rate) || 1;
            
            const calculatedData = calculateFormData(newData, conversionRate);
            return {
                ...newData,
                ...calculatedData
            };
        });
    };


    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Create Purchase Invoice</h5>
                </div>
                <div className="d-flex justify-content-end">
                    {/* <LanguageSwitch onLanguageChange={setSelectedLanguage} /> */}
                </div>
            </div>
            <div className="flex-grow-1">
                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="invoice_number"
                            label="Invoice Number"
                            value={formData.invoice_number}
                            onChange={handleChange}
                            placeholder="Enter invoice number"
                            disabled={true}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonAsyncSelect
                            name="supplier"
                            label="Supplier"
                            value={formData.supplier}
                            onChange={(value) => handleSelectChange('supplier', value)}
                            loadOptions={async (inputValue) => {
                                const response = await supplierService.getList({ search: inputValue });
                                return response.data.map(item => ({
                                    id: item.id,
                                    label: item.name?.en || '',
                                    currency: item.currency,
                                    country: item.country,
                                    vat_group: item.vat_group,
                                    is_domestic: item.is_domestic,
                                    address_name: item.address_name,
                                    address: item.address,
                                }));
                            }}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonDate
                            name="invoice_date"
                            label="Invoice Date"
                            value={formData.invoice_date}
                            onChange={handleChange}
                            placeholder="Enter invoice date"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonSelect
                            name="country"
                            label="Country"
                            value={formData.country}
                            onChange={(value) => handleSelectChange('country', value)}
                            options={countries}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select country..."
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="currency"
                            label="Currency"
                            value={formData.currency}
                            onChange={(value) => handleSelectChange('currency', value)}
                            options={currencies}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={true}
                            placeholder="Select currency..."
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="conversion_rate"
                            label="Conversion Rate"
                            value={formData.conversion_rate}
                            onChange={handleChange}
                            placeholder="Enter conversion rate"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonSelect
                            name="location"
                            label="Location"
                            value={formData.location}
                            onChange={(value) => handleSelectChange('location', value)}
                            options={locations}
                            isSubmitting={isSubmitting}
                            errors={errors}
                            required={false}
                            placeholder="Select location..."
                        />
                    </div>

                    <div className="col-md-4">
                        <TranslatableInput
                            label="Address 1"
                            name="address1"
                            value={formData.address1}
                            onChange={handleChange}
                            errors={errors}
                            required={true}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableTextarea
                            label="Address 2"
                            name="address2"
                            rows={1}
                            value={formData.address2}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="purchase_number"
                            label="Purchase Number"
                            value={formData.purchase_number}
                            onChange={handleChange}
                            placeholder="Enter purchase number"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonDate
                            name="po_date"
                            label="PO Date"
                            value={formData.po_date}
                            onChange={handleChange}
                            placeholder="Enter po date"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>

                    <div className="col-md-4">
                        <CommonInput
                            name="phone_number"
                            label="Phone Number"
                            value={formData.phone_number}
                            onChange={handleChange}
                            placeholder="Enter phone number"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="quotation_number"
                            label="Quotation Number"
                            value={formData.quotation_number}
                            onChange={handleChange}
                            placeholder="Enter quotation number"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonDate
                            name="quotation_date"
                            label="Quotation Date"
                            value={formData.quotation_date}
                            onChange={handleChange}
                            placeholder="Enter quotation date"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="sup_order_number"
                            label="Supplier Order Number"
                            value={formData.sup_order_number}
                            onChange={handleChange}
                            placeholder="Enter supplier order number"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="sup_invoice_number"
                            label="Supp. Inv. Number"
                            value={formData.sup_invoice_number}
                            onChange={handleChange}
                            placeholder="Enter supplier invoice number"
                            disabled={isSubmitting}
                            errors={errors}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="vat_business_group"
                            label="VAT Business Group"
                            value={formData.vat_business_group}
                            onChange={(value) => handleSelectChange('vat_business_group', value)}
                            options={[]}
                            isSubmitting={true}
                            errors={errors}
                            required={false}
                            placeholder="Select vat business group..."
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <TranslatableInput
                            label="Reference"
                            name="reference"
                            value={formData.reference}
                            onChange={handleChange}
                            errors={errors}
                            required={false}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableTextarea
                            label="Remarks"
                            name="remarks"
                            rows={1}
                            value={formData.remarks}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableTextarea
                            label="Terms"
                            name="terms"
                            rows={1}
                            value={formData.terms}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                        />
                    </div>
                </div>

                <div className="row">
                    <Tab.Container defaultActiveKey="items">
                        <Nav variant="tabs" className="mb-3">
                            <Nav.Item className="custom-li-item p-0">
                                <Nav.Link eventKey="items">Purchase Items</Nav.Link>
                            </Nav.Item>
                            <Nav.Item className="custom-li-charges p-0">
                                <Nav.Link eventKey="charges">Additional Charges</Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <Tab.Content>
                            <Tab.Pane eventKey="items" className="p-0">
                                {hasPermission && (
                                <button type="button" className="btn btn-primary" onClick={() => itemModal.show()}>
                                    Add Item
                                </button>
                                )}

                                <div className="table-responsive mt-4">
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th className="text-nowrap p-3">Item Code</th>
                                                <th className="text-nowrap p-3">Item Name</th>
                                                <th className="text-nowrap p-3">Unit</th>
                                                <th className="text-nowrap p-3">Qty Received</th>
                                                <th className="text-nowrap p-3">Qty Ordered</th>
                                                <th className="text-nowrap p-3">Unit Price</th>
                                                <th className="text-nowrap p-3">Discount</th>
                                                <th className="text-nowrap p-3">Line Amount</th>
                                                <th className="text-nowrap p-3">VAT Amount</th>
                                                <th className="text-nowrap p-3">Header Discount</th>
                                                <th className="text-nowrap p-3">Landed Cost</th>
                                                <th className="text-nowrap p-3">Landed Line Total</th>
                                                <th className="text-nowrap p-3">Landed Cost LCY</th>
                                                <th className="text-nowrap p-3">Landed Line Total LCY</th>
                                                <th className="text-nowrap p-3">Amt Excl VAT</th>
                                                <th className="text-nowrap p-3">Amt Incl VAT</th>
                                                <th className="text-nowrap p-3">VAT Product Group</th>
                                                <th className="text-nowrap p-3" style={{ minWidth: '100px' }}>VAT %</th>
                                                <th className="text-nowrap p-3">Remove</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.items && formData.items.length > 0 ? (
                                                formData.items.map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="text-nowrap p-3 align-middle">{item.item?.item_code || 'N/A'}</td>
                                                        <td className="text-nowrap p-3 align-middle">{item.item?.label || 'N/A'}</td>
                                                        <td className="p-3 align-middle">{item.unit || 'N/A'}</td>
                                                        <td className="p-3 align-middle">
                                                            <ItemInput
                                                                index={index}
                                                                field="quantity_received"
                                                                value={item.quantity_received}
                                                                onChange={handleChange}
                                                                disabled={isSubmitting}
                                                                errors={errors}
                                                                type="number"
                                                                displayError={false}
                                                            />
                                                        </td>
                                                        <td className="p-3 align-middle">
                                                            <CommonInput
                                                                className="mb-0"
                                                                name={`items[${index}].quantity_ordered`}
                                                                value={item.quantity_ordered}   
                                                                onChange={handleChange}
                                                                disabled={isSubmitting}
                                                                errors={errors}
                                                            />
                                                        </td>
                                                        <td className="p-3 align-middle">
                                                            <CommonInput
                                                                className="mb-0"
                                                                name={`items[${index}].unit_price`}
                                                                value={item.unit_price}
                                                                onChange={handleChange}
                                                                disabled={isSubmitting}
                                                                errors={errors}
                                                            />
                                                        </td>
                                                        <td className="p-3 align-middle">
                                                            <CommonInput
                                                                className="mb-0"
                                                                name={`items[${index}].discount_percentage`}
                                                                value={item.discount_percentage}
                                                                onChange={handleChange}
                                                                disabled={isSubmitting}
                                                                errors={errors}
                                                            />
                                                        </td>
                                                        <td className="p-3 align-middle">{item.line_amount || '0.00'}</td>
                                                        <td className="p-3 align-middle">{item.vat_amount || '0.00'}</td>
                                                        <td className="p-3 align-middle">{item.header_discount || '0.00'}</td>
                                                        <td className="p-3 align-middle">{item.landed_cost || '0.00'}</td>
                                                        <td className="p-3 align-middle">{item.landed_line_total || '0.00'}</td>
                                                        <td className="p-3 align-middle">{item.landed_cost_lcy || '0.00'}</td>
                                                        <td className="p-3 align-middle">{item.landed_line_total_lcy || '0.00'}</td>
                                                        <td className="p-3 align-middle">{item.amount_excl_vat || '0.00'}</td>
                                                        <td className="p-3 align-middle">{item.amount_incl_vat || '0.00'}</td>
                                                        <td className="p-3 align-middle">{item.vat_product_group?.label || 'N/A'}</td>
                                                        <td className="p-3 align-middle">
                                                            <CommonInput
                                                                className="mb-0"
                                                                name={`items[${index}].vat_percentage`}
                                                                value={item.vat_percentage}
                                                                onChange={handleChange}
                                                                disabled={isSubmitting}
                                                                errors={errors}
                                                            />
                                                        </td>
                                                        <td className="p-3 align-middle">
                                                            <div>
                                                                <button 
                                                                    type="button"
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => handleRemoveItem(index)}
                                                                    disabled={isSubmitting}
                                                                >
                                                                    <i className="fas fa-trash text-white"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="19" className="text-center">No items added</td>
                                                </tr>
                                            )}
                                        </tbody>    
                                    </table>
                                </div>

                                <div className="row mt-4">
                                    <div className="col-md-6">
                                        <div className="mb-2 row">
                                            <label className="col-lg-4 col-md-4 col-form-label"></label>
                                            <div className="col-lg-4 col-md-4 font-weight-bold">FCY</div>
                                            <div className="col-lg-4 col-md-4 font-weight-bold">LCY</div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-4 col-md-4 col-form-label">Gross Total:</label>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="gross_total"
                                                    value={formData.gross_total}
                                                    onChange={handleChange}
                                                    disabled={true}
                                                    errors={errors}
                                                />  
                                            </div>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="gross_total_lcy"
                                                    value={formData.gross_total_lcy}
                                                    onChange={handleChange}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-4 col-md-4 col-form-label">Discount %:</label>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    type="number"
                                                    className="mb-0"
                                                    name="discount_percentage"
                                                    value={formData.discount_percentage}
                                                    onChange={handleChange}
                                                    disabled={isSubmitting}
                                                    errors={errors}
                                                    max={100}
                                                />
                                            </div>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="discount_percentage_lcy"
                                                    value={formData.discount_percentage}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-4 col-md-4 col-form-label">Discount Amt:</label>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="discount_amount"
                                                    value={formData.discount_amount}
                                                    onChange={handleDiscountChange}
                                                    disabled={isSubmitting}
                                                    errors={errors}
                                                />
                                            </div>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="discount_amount_lcy"
                                                    value={formData.discount_amount_lcy}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-4 col-md-4 col-form-label">Sub Total:</label>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="sub_total"
                                                    value={formData.sub_total}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="sub_total_lcy"
                                                    value={formData.sub_total_lcy}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-4 col-md-4 col-form-label">VAT Amount:</label>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="vat_amount"
                                                    value={formData.vat_amount}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="vat_amount_lcy"
                                                    value={formData.vat_amount_lcy}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-4 col-md-4 col-form-label">Net Total:</label>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="net_total"
                                                    value={formData.net_total}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="net_total_lcy"
                                                    value={formData.net_total_lcy}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-2 row">
                                            <label className="col-lg-4 col-md-4 col-form-label"></label>
                                            <div className="col-lg-4 col-md-4 font-weight-bold">FCY</div>
                                            <div className="col-lg-4 col-md-4 font-weight-bold">LCY</div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-4 col-md-4 col-form-label">Total Charges:</label>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="total_charges"
                                                    value={formData.total_charges}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="total_charges_lcy"
                                                    value={formData.total_charges_lcy}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-4 col-md-4 col-form-label">Supplier Charges:</label>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="supplier_charges"
                                                    value={formData.supplier_charges}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="supplier_charges_lcy"
                                                    value={formData.supplier_charges_lcy}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-4 col-md-4 col-form-label">Supplier Total:</label>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="supplier_total"
                                                    value={formData.supplier_total}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="supplier_total_lcy"
                                                    value={formData.supplier_total_lcy}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-4 col-md-4 col-form-label">Total Cost:</label>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="total_cost"
                                                    value={formData.total_cost}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonInput
                                                    className="mb-0"
                                                    name="total_cost_lcy"
                                                    value={formData.total_cost_lcy}
                                                    disabled={true}
                                                    errors={errors}
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-2 row">
                                            <label className="col-lg-4 col-md-4 col-form-label">Is Posted Invoice?</label>
                                            <div className="col-lg-4 col-md-4">
                                                <CommonCheckbox
                                                    name="posted_invoice"
                                                    label=""
                                                    value={formData.posted_invoice}
                                                    onChange={handleChange}
                                                    errors={errors}
                                                    disabled={formData.is_posted}
                                                />
                                            </div>
                                        </div>
                                        {
                                            (formData.posted_invoice) && (
                                                <div className="mb-2 row">
                                                    <label className="col-lg-4 col-md-4 col-form-label">Posted Date:</label>
                                                    <div className="col-lg-4 col-md-4">
                                                        <CommonDate
                                                            name="post_date"
                                                            label=""
                                                            value={formData.post_date}
                                                            onChange={handleChange}
                                                            placeholder="Enter Posted date"
                                                            disabled={formData.is_posted}
                                                            errors={errors}
                                                        />
                                                    </div>
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="charges" className="p-0">
                                <div className="row mt-4">
                                    <div className="col-md-12">
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Cost Code</th>
                                                    <th>Cost Description</th>
                                                    <th>Amount</th>
                                                    <th>GL Code</th>
                                                    <th>Supplier Flag</th>
                                                    <th>Offset Purchase VAT</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.additional_charges && formData.additional_charges.length > 0 ? (
                                                    formData.additional_charges.map((additional, index) => (
                                                        <tr key={index}>
                                                        <td>{additional.cost_code}</td>
                                                        <td>{additional.cost_description?.en}</td>
                                                        <td>
                                                            <CommonInput
                                                                className="mb-0"
                                                                name={`additional_charges[${index}].amount`}
                                                                value={formData.additional_charges[index].amount}
                                                                onChange={handleChange}
                                                                disabled={isSubmitting}
                                                                errors={errors}
                                                            />
                                                        </td>
                                                        <td>{additional.gl_code}</td>
                                                        <td>{additional.supplier_flag ? 'Yes' : 'No'}</td>
                                                        <td>{additional.purchase_vat ? 'Yes' : 'No'}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center">No additional charges found</td>
                                                </tr>
                                            )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </div>

                
                <DataSelectionModal
                    show={itemModal.isVisible}
                    onHide={itemModal.hide}
                    title=""
                    DataComponent={Items}
                    selectionMode="multiple"
                    onRowsSelected={handleItemSelected}
                    selectionButtonText="Add Items"
                    selectionButtonIcon="fa fa-shopping-cart"
                    componentProps={{
                        isMultipleSelection: true,
                        excludedActions: ['create', 'actions', 'filter'],
                        externalCondition: {
                            rules: {
                                condition: 'and',
                                children: [
                                    {
                                        type: 'rule',
                                        query: {
                                            operator: 'in',
                                            rule: 'items.item_type',
                                            type: 'text',
                                            value: ['ready_made', 'raw_material']
                                        }
                                    }
                                ]
                            }
                        }
                    }}
                />
            </div>

            {hasPermission && (
            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading...
                        </>
                    ) : 
                    formData.is_posted ? (
                        <>
                            <i className="fa fa-lock me-2" /> Locked
                        </>
                    ) :
                    isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
            )}
        </form>
    );
};

export default PurchaseInvoiceForm; 